import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useBetween } from "use-between";
import { mutate } from "swr";
import dayjs from "dayjs";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { emailRegExpTest } from "@aclymatepackages/reg-exp";
import TranslatedYesNoQuestion from "../atoms/TranslatedYesNoQuestion";
import { formatDate } from "@aclymatepackages/formatters";
import { incorrectNameCheck } from "@aclymatepackages/other-helpers";

import { useSurveyLanguage } from "../../helpers/contexts/surveyLanguage";

import { useNewVehiclesForm } from "./vehicles";

import BuildWorkspacesForm from "../inputs/employees/BuildWorkspacesForm";

import DatePicker from "../atoms/mui/DatePicker";
import PlacesAutocomplete from "../inputs/autocomplete/PlacesAutocomplete";

import { isObjectEmpty } from "../../helpers/otherHelpers";
import { getAccountId } from "../../helpers/accountHelpers";
import { useSharedFormLoading } from "../../helpers/components/inputs";
import {
  fetchOurApi,
  findPropertyDetails,
  fetchNearestAirportFromCoordinates
} from "../../helpers/utils/apiCalls";
import { findGoogleAddressStringDetails } from "../../helpers/utils/geography";
import { useCachedFirebaseCrud } from "../../helpers/firebase";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";

const setEmployeeAddress = (setEmployee) => async (place) => {
  if (place) {
    const { place_id, description } = place;
    const { zipCode, city, state, country, coordinates } =
      await findGoogleAddressStringDetails(place_id);

    findPropertyDetails({
      description,
      city,
      state,
      zipCode
    }).then(({ error, id: realtyMolePropertyId, squareFootage }) => {
      if (!error) {
        setEmployee("homeSqFootage")(squareFootage);
        return setEmployee("realtyMolePropertyId")(realtyMolePropertyId);
      }

      return null;
    });

    await fetchNearestAirportFromCoordinates(coordinates, (res) =>
      setEmployee("defaultAirport")(res)
    );

    return setEmployee("address")({
      description: description,
      zipCode,
      state,
      country,
      coordinates,
      placeId: place_id
    });
  }
  return setEmployee("address")(null);
};

const useNewEmployee = () => {
  const [newEmployee, setNewEmployee] = useState({});
  const [employeeVehicles, setEmployeeVehicles] = useState([]);

  return {
    newEmployee,
    setNewEmployee,
    employeeVehicles,
    setEmployeeVehicles
  };
};

export const useSharedNewEmployee = () => useBetween(useNewEmployee);

const usePersonalInfoForm = ({
  survey,
  givenEmployee,
  setGivenEmployee,
  companyStartDate,
  mostRecentAccountingDate
}) => {
  const { newEmployee, setNewEmployee } = useSharedNewEmployee();
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";

  const editNewEmployee = (field) => (value) =>
    editObjectData(setGivenEmployee || setNewEmployee, field, value);

  const [employeeEmailWarningText, setEmployeeEmailWarningText] =
    useState(null);

  const [isStartDateNotNeeded, setIsStartDateNotNeeded] = useState();

  const employeeData = givenEmployee || newEmployee || {};
  const { address, email, name, startDate, firstName, lastName } = employeeData;
  const { description } = address || {};

  const editEmployeeEmail = (email) => {
    editNewEmployee("email")(email);

    if (email && !emailRegExpTest(email)) {
      return setEmployeeEmailWarningText(translate("personalInfo.emailError"));
    }

    return setEmployeeEmailWarningText(null);
  };

  const splitFullName = (fullName) => {
    if (!fullName) {
      return { firstName: "", lastName: "" };
    }

    const parts = fullName.trim().split(/\s+/);
    const [first, ...rest] = parts;
    const remainder = rest.join(" ");

    return { firstName: first || "", lastName: remainder || "" };
  };

  const composeFullName = (first, last) => {
    return [first, last].filter(Boolean).join(" ").trim();
  };

  const editFirstName = (value) => {
    const currentLast = lastName || "";
    const combined = composeFullName(value, currentLast);

    editNewEmployee("firstName")(value);
    return editNewEmployee("name")(combined);
  };

  const editLastName = (value) => {
    const currentFirst = firstName || "";
    const combined = composeFullName(currentFirst, value);

    editNewEmployee("lastName")(value);
    return editNewEmployee("name")(combined);
  };

  const isStartDateNeededRow = !mostRecentAccountingDate
    ? [
        {
          value: isStartDateNotNeeded !== undefined,
          input: (
            <TranslatedYesNoQuestion
              question={
                useYouForm
                  ? translate("personalInfo.startedBefore", {
                      date: formatDate(companyStartDate)
                    })
                  : `Did this employee start working here before ${formatDate(
                      companyStartDate
                    )}?`
              }
              value={isStartDateNotNeeded}
              setValue={(value) => {
                setIsStartDateNotNeeded(value);

                return editNewEmployee("startDate")(null);
              }}
            />
          )
        }
      ]
    : [];

  const startDateInputRow = !isStartDateNotNeeded
    ? [
        {
          value: startDate,
          input: (
            <DatePicker
              label={
                useYouForm
                  ? translate("personalInfo.startDate")
                  : "When did this employee start here?"
              }
              date={startDate}
              minDate={
                mostRecentAccountingDate &&
                dayjs(mostRecentAccountingDate).add(1, "day").toDate()
              }
              maxDate={new Date()}
              editDate={editNewEmployee("startDate")}
              inputVariant="standard"
              darkTheme
            />
          )
        }
      ]
    : [];

  const splitFromLegacy = splitFullName(name);
  const displayFirstName = firstName || splitFromLegacy.firstName;
  const displayLastName = lastName || splitFromLegacy.lastName;

  return {
    personalInfoForm: [
      {
        label: useYouForm
          ? translate("personalInfo.firstName")
          : "What is this employee's first name?",
        editData: editFirstName,
        value: displayFirstName,
        error: displayFirstName && incorrectNameCheck(displayFirstName)
      },
      {
        label: useYouForm
          ? translate("personalInfo.lastName")
          : "What is this employee's last name?",
        editData: editLastName,
        value: displayLastName,
        error: displayLastName && incorrectNameCheck(displayLastName)
      },
      {
        label: useYouForm
          ? translate("personalInfo.email")
          : "What is this employee's email address?",
        value: email,
        error: !email,
        editData: editEmployeeEmail
      },
      {
        value: address,
        input: (
          <PlacesAutocomplete
            place={description || ""}
            label={
              useYouForm
                ? translate("personalInfo.homeAddress")
                : "What is this employee's home address, zip code, or city that they live in?"
            }
            editPlace={setEmployeeAddress(editNewEmployee)}
            size="small"
            variant="standard"
            id="newEmployeeAddressField"
          />
        )
      },
      ...isStartDateNeededRow,
      ...startDateInputRow
    ],
    employeeEmailWarningText
  };
};

const addSimulationEmployee = ({
  otherEmployeeProps,
  startDate,
  address,
  homeSqFootage,
  realtyMolePropertyId,
  vehiclesArray,
  commuteSchedules,
  companyId
}) => {
  const confirmedEmployee = {
    ...otherEmployeeProps,
    ...(!!startDate ? { startDate: dayjs(startDate).toDate() } : {}),
    ...(!!address ? { address } : {}),
    ...(!!homeSqFootage ? { homeSqFootage } : {}),
    ...(!!realtyMolePropertyId ? { realtyMolePropertyId } : {}),

    vehicles: vehiclesArray,
    commuteSchedules,

    links: [
      {
        id: companyId,
        name: "Aclymate",
        status: "active",
        employeeStatus: "confirmed",
        startDate: !!startDate ? dayjs(startDate).toDate() : new Date()
      }
    ],

    currentLinkedIds: [companyId],

    origin: "employee",

    slug: `${otherEmployeeProps.firstName || ""}-${
      otherEmployeeProps.lastName || ""
    }`
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase(),

    dateCreated: new Date(),

    companyId,
    status: "unconfirmed",
    createdAt: new Date(),
    updatedAt: new Date(),
    id: `emp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  };

  return confirmedEmployee;
};

export const useNewEmployeeForm = ({
  onClose = () => {},
  existingEmployee,
  setParentStep,
  surveyThankYouStep,
  companyStartDate,
  mostRecentAccountingDate
}) => {
  const { isSimulation } = usePlatformLayoutContext() || {};
  const { newCollectionDoc } = useCachedFirebaseCrud();
  const { translate, language } = useSurveyLanguage();
  const useYouForm = surveyThankYouStep || language !== "en";
  const paramsCompanyId = useParams()?.companyId;
  const id = getAccountId();
  const companyId = paramsCompanyId || id;

  const { newEmployee, employeeVehicles, setNewEmployee, setEmployeeVehicles } =
    useSharedNewEmployee();

  useEffect(() => {
    if (existingEmployee) {
      setNewEmployee(existingEmployee);
    }
  }, [existingEmployee, setNewEmployee]);

  const { name } = newEmployee;

  const { personalInfoForm, employeeEmailWarningText } = usePersonalInfoForm({
    survey: surveyThankYouStep,
    companyStartDate,
    mostRecentAccountingDate
  });

  const { setFormLoading } = useSharedFormLoading();

  const newEmployeeSubmit = async (commuteSchedules) => {
    if (!surveyThankYouStep) {
      setFormLoading(true);
    }

    const vehiclesArray = isObjectEmpty(employeeVehicles[0])
      ? []
      : employeeVehicles;

    const {
      address,
      startDate,
      homeSqFootage,
      realtyMolePropertyId,
      ...otherEmployeeProps
    } = newEmployee;

    const sharedEmployeeProps = {
      employeeVehicles: vehiclesArray,
      commuteSchedules
    };

    if (isSimulation) {
      const simulationEmployee = addSimulationEmployee({
        otherEmployeeProps,
        startDate,
        address,
        homeSqFootage,
        realtyMolePropertyId,
        vehiclesArray,
        commuteSchedules,
        companyId
      });

      newCollectionDoc("employees", simulationEmployee);

      if (setParentStep) {
        return setParentStep();
      }

      mutate("employees", (currentEmployees = []) => [
        ...currentEmployees,
        simulationEmployee
      ]);

      return onClose();
    }

    //This process runs in the back-end instead of the front-end to allow for unauthenticated users in the employee survey
    const confirmedEmployee = await fetchOurApi({
      accountId: companyId,
      path: "/individuals/new-employee",
      method: "POST",
      data: {
        employee: {
          ...otherEmployeeProps,
          ...(!!startDate ? { startDate: dayjs(startDate).toDate() } : {}),
          ...(!!address ? { address } : {}),
          ...(!!homeSqFootage ? { homeSqFootage } : {}),
          ...(!!realtyMolePropertyId ? { realtyMolePropertyId } : {})
        },
        ...sharedEmployeeProps,
        companyId
      },
      callback: (response) => response
    });

    if (setParentStep) {
      return setParentStep();
    }

    mutate("employees", (currentEmployees = []) => [
      ...currentEmployees,
      confirmedEmployee
    ]);

    return onClose();
  };

  const newVehiclesForm = useNewVehiclesForm({
    newEmployeeName: name,
    newVehicles: employeeVehicles,
    setNewVehicles: setEmployeeVehicles,
    survey: surveyThankYouStep
  });

  return [
    {
      label: useYouForm
        ? translate("personalInfo.label")
        : "Personal Information",
      title: useYouForm
        ? translate("personalInfo.title")
        : "Let's start with some basic personal information",
      rows: personalInfoForm,
      formAdvanceErrorMessage: employeeEmailWarningText
    },
    ...newVehiclesForm,
    {
      form: (
        <BuildWorkspacesForm
          onSubmit={newEmployeeSubmit}
          surveyThankYouStep={surveyThankYouStep}
        />
      )
    }
  ];
};
